const PENDING_ENTRA_LOGOUT_KEY = 'gmg.pendingEntraLogout';

const clearPendingEntraLogout = (): void => {
    try {
        sessionStorage.removeItem(PENDING_ENTRA_LOGOUT_KEY);
    } catch {
        // If the browser blocks removal, continue anyway. This saved value should not
        // stop Cognito logout or stop the app from opening when Entra is turned off.
    }
};

type LogoutConfig = {
    FeatureFlags?: { isEntraLoginEnabled?: boolean };
    GMGHostedAuth: {
        entraLogoutUrl?: string;
        oauth: { redirectSignOut: string };
    };
};

export const getEntraLogoutUrl = (config: LogoutConfig): string => {
    const endpoint = config.GMGHostedAuth.entraLogoutUrl;
    if (!endpoint) {
        throw new Error('Entra logout requires entraLogoutUrl.');
    }
    const url = new URL(endpoint);
    if (url.protocol !== 'https:') {
        throw new Error('entraLogoutUrl must use HTTPS.');
    }
    const returnUrl = config.GMGHostedAuth.oauth.redirectSignOut;
    if (new URL(returnUrl).origin !== window.location.origin) {
        throw new Error('Entra logout requires a same-origin Cognito sign-out return URL.');
    }
    url.searchParams.set('post_logout_redirect_uri', returnUrl);
    return url.toString();
};

export const signOutWithEntra = async (
    config: LogoutConfig,
    signOutOfCognito: () => Promise<void>,
): Promise<void> => {
    if (config.FeatureFlags?.isEntraLoginEnabled === true) {
        // Check the logout address before leaving the app.
        getEntraLogoutUrl(config);
        try {
            // Save a reminder to log out of Entra when Cognito sends the user back to the app.
            sessionStorage.setItem(PENDING_ENTRA_LOGOUT_KEY, 'pending');
        } catch {
            // If we cannot save the reminder, still log out of Cognito.
            // First warn the user that their Microsoft account may stay signed in.
            window.alert('Microsoft Entra sign-out could not be prepared because browser session storage is unavailable. '
                + 'Application sign-out will continue, but your Microsoft session may remain active. '
                + 'Signing in again may reuse the same Microsoft account.');
        }
    } else {
        // Entra is turned off. Try to remove any old reminder, but let logout continue if removal fails.
        clearPendingEntraLogout();
    }

    try {
        // Log out of Cognito even if saving the Entra reminder failed.
        await signOutOfCognito();
    } catch (error) {
        // If Cognito logout fails, try to remove the reminder and report the Cognito error.
        clearPendingEntraLogout();
        throw error;
    }
};

export const consumePendingEntraLogout = (config: LogoutConfig): string | undefined => {
    // Check whether Entra is turned on before reading the saved logout reminder.
    // If it is off, let the app open even if the browser does not allow access to sessionStorage.
    if (config.FeatureFlags?.isEntraLoginEnabled !== true) {
        clearPendingEntraLogout();
        return undefined;
    }

    // If Entra is on and we cannot read the reminder, report the error instead of continuing.
    // Otherwise, the app could skip Microsoft logout and sign the same user back in.
    const pending = sessionStorage.getItem(PENDING_ENTRA_LOGOUT_KEY);
    if (!pending) {
        return undefined;
    }

    // Use the logout address from the app settings, not an address saved in browser storage.
    const url = getEntraLogoutUrl(config);
    // Once the address is checked, remove the reminder so returning to the app does not repeat logout.
    sessionStorage.removeItem(PENDING_ENTRA_LOGOUT_KEY);
    return url;
};
